<template>
  <div class="common-table">
    <BaseTableCollapsibleRow
      v-if="masterSum && showSummary"
      class="common-table__header-master"
      :data="masterSumData"
      :config="[sumConfig]"
      :level="0"
      :paddingLeft="paddingLeft"
    />
    <BaseTableCollapsibleRow
      v-if="showSummary"
      class="common-table__header-filtered"
      :data="filteredSumData"
      :config="[sumConfig]"
      :level="0"
      :paddingLeft="paddingLeft"
    />
    <div class="common-table__row common-table__header"
         :class="{'common-table__header_scrolled': isScrollbarExist}"
         :style="headerStyle">
      <div class="common-table__header-subtitle" :style="subTitleStyle" v-if="!isKeywordsModal">Rank Zone</div>
      <div :style="{ ...firstCell.style, ...paddingStyle }"
           class="common-table__header-cell"
           @click.exact="onHeaderCellClicked(firstCell)"
           @click.shift="onHeaderCellClicked(firstCell, true)">
        {{firstCell.label}}
        <span class="common-table__count" v-if="count && !isKeywordsModal">{{subtitle}}</span>
        <base-table-sortable-column
          v-if="sorted"
          alignCenter
          :prop-name="firstCell.propName"
          :sort-config="sortConfig"/>
      </div>
      <div v-for="(cell) in otherCells"
           :style="cell.style"
           class="common-table__header-cell common-table__header-cell_other"
           :class="{'common-table__header-cell_shifted': !isKeywordsModal}"
           @click.exact="onHeaderCellClicked(cell)"
           @click.shift="onHeaderCellClicked(cell, true)">
        {{cell.label}}

        <base-table-sortable-column
          v-if="sorted"
          alignCenter
          :prop-name="cell.propName"
          :sort-config="sortConfig"/>
      </div>
      <div :style="lastCell.style"
           class="common-table__header-cell"
           @click.exact="onHeaderCellClicked(lastCell)"
           @click.shift="onHeaderCellClicked(lastCell, true)">
        {{lastCell.label}}
        <base-table-sortable-column
          v-if="sorted"
          alignCenter
          :prop-name="lastCell.propName"
          :sort-config="sortConfig"/>
      </div>
    </div>
    <div :class="{'common-table__row-container_scrollable': isScrollable}"
         ref="table-container">
      <template v-if="data.length">
        <div v-for="(row) in sortedData" :key="getRowId(row)">
          <BaseTableCollapsibleRow
            :truncateUrl="truncateUrl"
            :data="row"
            @cellClick="handleCellClick"
            :config="config"
            :level="0"
            :paddingLeft="paddingLeft"
            :show-calendar="showCalendar"
            :showLinkToCB="showLinkToCB"
            :is-keywords-modal="isKeywordsModal"
            ref="table-row"
          />
        </div>
      </template>
      <div v-else-if="!isShowLoader"
           class="common-table__not-found">No records found
      </div>
      <div v-if="isShowLoader"
           class="common-table__not-found">
        <base-loader />
      </div>
    </div>
    <modal :name="keywordMovementModalName"
           v-bind="modalProps"
    >
      <base-keyword-history-modal :comparison="comparisonArray"
                                  :keyword="selectedKeyword"
                                  @close="closeModal"/>
    </modal>
  </div>
</template>

<script>
import _ from 'lodash'
import helper from '@/services/Helper'
import BaseSortMixins from '@/common/BaseSortMixins'
import baseModalProps from '@/common/BaseModalProps'
import {mapState} from "vuex";
import BaseLoader from "@/common/BaseLoader";

/**
 * general component for tables with expanded multilevel rows
 * all configs received using сonfig props
 */
export default {
  name: 'BaseTableCollapsible',
  components: {BaseLoader},
  mixins: [BaseSortMixins],
  // return to default sorting if again call table
  created () {
    const config = _.cloneDeep(this.config)
    config.forEach(item => {
      if (Object.keys(item).includes('defaultSorting')) {
        item.defaultSorting = true
      }
    })
    this.config = config
    this.sortData()
  },
  mounted () {
    this.setScrollbarExist()
    window.addEventListener('resize', _.debounce(() => {
      this.setScrollbarExist()
    }, 100), false)
  },
  props: {
    /**
     *  table config
     *  @example:
     *  [{type: 'top-level',    //level
        defaultSorting: true,   // define if need additional sorting for this lvl (not working in 'top-level')
        sortingCell: 'msv',   // define default sorting column (works when defaultSorting === true)
        sortingDirection: helper.DIRECTION_DESC,    // define default sorting direction (works when defaultSorting === true)
        children: 'children',   //children property name
        isChildrenLoadingLevel: true, // is used to notify child component BaseTableCollapsibleRow about necessity to load child data from backend
        highlightProp: 'type', // is used when we need to change color of concrete cell with propName == highlightProp and cell content == highlightValue
        highlightValue: 'Siblings',
        colorMap: [],           // color indicators list for first column. is used on Competition.vue
        colorProperty: 'date',   // name of trigger property (propName) to change indicator color
        cells: [                // array of table cells with column settings
          {label: 'URL Status', // label of first column
            subtitleSingle: 'status found',   // subtitle name settings
            subtitlePlural: 'statuses found', // subtitle name settings
            propName: 'label',   // name of property in received form backend data
            style: {              // style stuff
              'flex-basis': '33%'
            }
          },
          {label: '01 - 03',
            propName: '01 - 03',
            filterName: 'commaDividedNumber'  // name filter function that should be applied to value in the table cell
            },...]},
        {type: 'second-level',
        children: 'children',
        childrenLimit: 20,    //max length of children inner table if we are using inner pagination for children
        cells: [
          {propName: 'label',
            style: {
              'flex-basis': '33%'}
          },
          {propName: '01 - 03',
            filterName: 'commaDividedNumber'
          },...]
          }]
     **/
    config: {
      type: Array,
      required: true
    },
    /* table data */
    data: {
      type: Array,
      required: true
    },
    paddingLeft: {
      type: Number,
      default: 0
    },
    headerStyle: {
      type: Object,
      default: () => ({})
    },
    /* count for subtitle */
    count: {
      type: Number,
      default: null
    },
    /* data for master sum header */
    masterSum: {
      type: Array,
      default: null
    },
    /* is this table sortable */
    sorted: {
      type: Boolean,
      default: true
    },
    /* should be summary header be shown (master sum or filter sum) */
    showSummary: {
      type: Boolean,
      default: true
    },
    /* has this table external pagination or not */
    paginatedTable: {
      type: Boolean,
      default: false
    },
    /* data for filter sum header */
    filteredSum: {
      type: Object,
      default: () => {}
    },
    /* is used to make the table scrollable in case we restrict table height in parent component. */
    isScrollable: Boolean,
    /* is used to show/hide calendar icon on row. User on RankZone Url Tab */
    showCalendar: {
      type: Boolean,
      default: true
    },
    /* is used to show/hide CB icon on row. User on RankZone Url Tab */
    showLinkToCB: {
      type: Boolean,
      default: true
    },
    /* is used for customization table on Keywords modal (Content Brief - Reporting tab)  */
    isKeywordsModal: {
      type: Boolean,
      default: true
    },
    isShowLoader: {
      type: Boolean,
      default: false
    },
    /* is used for truncating table first column  */
    truncateUrl: {
      type: Boolean,
      default: false
    },
    webPropertyGroupId: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      isScrollbarExist: false,
      selectedKeyword: '',
      sortedData: [],
      // uses to avoid open modal twice in component duplicates
      keywordMovementModalName: Math.random().toString(36).substring(7)
    }
  },
  watch: {
    sortConfig: {
      handler () {
        if (!this.isShiftPressed && this.paginatedTable) {
          this.$emit('sort', this.sortConfig)
          return
        }
        this.isShiftPressed = false
        this.sortData()
      },
      deep: true,
      immediate: true
    },
    data: {
      handler () {
        if (this.paginatedTable) {
          this.sortedData = this.data
        } else {
          this.sortData()
        }
      },
      deep: true
    }
  },
  computed: {
    ...mapState('rankZoneCommon', {
      comparisonArray (state) {
        return !this.webPropertyGroupId ||
        (this.webPropertyGroupId.length &&
        (_.cloneDeep(state.webPropertyGroups).find(i => i._id === this.webPropertyGroupId) || {}).type === 'Clients') ? [] : [this.webPropertyGroupId]
      }
    }),
    topLevelConfig () {
      return this.config[0]
    },
    /* config for sum header BaseTableCollapsibleRow */
    sumConfig () {
      const config = { cells: _.cloneDeep(this.config[0].cells) }
      config.type = 'header'
      return config
    },
    filteredSumData () {
      if (this.paginatedTable) {
        return this.filteredSum
      }
      const data = {}
      this.sumConfig.cells.forEach(cell => {
        if (cell.propName !== 'label') {
          data[cell.propName] = 0
        }
      })
      this.calculateFilteredSum(this.data, data)
      data.label = 'Filter summary'
      return data
    },
    modalProps () {
      const shareModalProps = {
        width: '75%'
      }
      return { ...baseModalProps, ...shareModalProps }
    },
    masterSumData () {
      if (!this.masterSum) {
        return
      }
      const data = {}
      this.sumConfig.cells.forEach(cell => {
        if (cell.propName !== 'label') {
          data[cell.propName] = this.masterSum[cell.propName]
        }
      })
      data.label = 'Master summary'
      return data
    },
    firstCell () {
      return this.topLevelConfig.cells[0]
    },
    lastCell () {
      const cells = this.topLevelConfig.cells
      return cells[cells.length - 1]
    },
    otherCells () {
      return this.topLevelConfig.cells.slice(1, -1)
    },
    subTitleStyle () {
      if (!this.firstCell || !this.lastCell) {
        return ''
      }
      const result = {}
      const firstCellWidth = this.firstCell.style['flex-basis'] || this.firstCell.style.width || 0
      const lastCellWidth = this.lastCell.style['flex-basis'] || this.lastCell.style.width || 0
      result.width = `calc(100% - ${firstCellWidth} - ${lastCellWidth})`
      result.left = firstCellWidth
      result.textAlign = 'center'
      return result
    },
    subtitle () {
      if (!this.count) {
        return ''
      }
      if (this.count > 1) {
        return `(${this.count} ${this.firstCell.subtitlePlural})`
      } else {
        return `(${this.count} ${this.firstCell.subtitleSingle})`
      }
    },
    paddingStyle () {
      if (this.paddingLeft !== 0) {
        return {
          paddingLeft: `${this.paddingLeft}px`
        }
      } else {
        return {}
      }
    }
  },
  methods: {
    setScrollbarExist () {
      const container = this.$refs['table-container']
      if (!this.isScrollable || !container) {
        return
      }
      this.isScrollbarExist = container.scrollHeight > container.clientHeight
    },
    /* we need it to track each node’s identity see {@link https://vuejs.org/v2/guide/list.html#Maintaining-State} */
    getRowId (row) {
      return row._id ? row._id : row.label
    },
    /* to collapse table outside the component */
    collapseTable () {
      this.$refs['table-row'] && this.$refs['table-row'].forEach(item => item.collapseTable())
    },
    handleCellClick ({ target, configLevel, cellConfig = {} }) {
      this.$emit('cellClick', { ...target, ...cellConfig })
      configLevel && configLevel.showHistory && this.openModal(target)
    },
    openModal (keyword) {
      this.selectedKeyword = keyword
      this.$modal.show(this.keywordMovementModalName)
    },
    closeModal () {
      this.selectedKeyword = ''
      this.$modal.hide(this.keywordMovementModalName)
    },
    calculateFilteredSum (data, acc) {
      data.forEach(item => {
        this.sumConfig.cells.forEach(conf => {
          acc[conf.propName] += item[conf.propName]
        })
      })
    },
    sortData () {
      if (!this.sortConfig.length || !this.sorted) {
        this.sortedData = this.data
        return
      }
      /* is used to sort multilevel data */
      const sortData = (data, param, direction, level = 1) => {
        helper.sortByParamMutation(data, param, direction)
        data.forEach(item => {
          if (item.children && item.children.length) {
            if (this.config[level].defaultSorting) {
              const { sortingCell, sortingDirection } = this.config[level]
              sortData(item.children, sortingCell, sortingDirection, level + 1)
            } else {
              const { propertyName, sortDirection } = this.sortConfig[0]
              sortData(item.children, propertyName, sortDirection)
            }
          }
        })
        if (this.config && this.config[level]) this.config[level].defaultSorting = false
      }

      const { propertyName, sortDirection } = this.sortConfig[0]
      const sortedData = _.cloneDeep(this.data)
      sortData(sortedData, propertyName, sortDirection)
      this.sortedData = sortedData
    }
  }
}
</script>

<style scoped lang="scss">
  .common-table {
    font-size: 13px;

    &__header {
      background-color: $rfx-pale-grey;

      &-cell {
        background-color: $rfx-quarter-march !important;
        display: flex;
        align-items: center;

        &:first-child {
          border-top-left-radius: 12px;
        }

        &:last-child {
          border-top-right-radius: 12px;
        }
      }

      &::v-deep .common-table__header-cell {
        color: #fff;
        display: flex;
      }

      &::v-deep .common-table__sort-arrow {
        color: #fff;
      }
    }

    &__header-master,
    &__header-filtered {
      /*background-color: rgba(1, 22, 39, 0.16);*/
      background-color: $rfx-pale-grey;

      &::v-deep .base-collapsible-table-cell__header {
        color: $rfx-light-march;
        font-weight: 400 !important;
      }
    }

    &__header-filtered {

      &::v-deep .base-collapsible-table-cell {
        border-bottom: none !important;
      }
    }

    &__row {
      background-color: $rfx-pale-grey;
      position: relative;
    }

    &__header-subtitle {
      position: absolute;
      height: 30px;
      top: 6px;
      z-index: 1;
      color: $rfx-dark-march;
      font-weight: 500;
    }

    &__header-cell {
      @include ellipsis;
      color: $rfx-dark-march;
      box-sizing: border-box;

      &_shifted {
        border-top: 1px solid rgba(0, 0, 0, .05);
        padding-top: 6px;
        padding-bottom: 0;
        margin-top: 28px;
      }

      &_other {
        padding-left: 0;
        padding-right: 0;
      }

      &:last-child {
        border-top: none;
        padding-left: 12px;
        padding-right: 0;
      }
      &:first-child {
        padding-left: 20px;
      }
    }

    &__not-found {
      font-size: 27px;
      font-weight: 600;
      display: flex;
      align-items: center;
      justify-content: center;
      height: 60px;
      color: $rfx-medium-grey;
    }

    &__count {
      color: $rfx-steel;
    }
  }

</style>
